<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': isDisplayed,
    }"
    :data-test="`session-slide-${index}`"
  >
    <template #text>
      <SlideText :text="prismicSlice.primary.text_body" />
      <div class="cta-button-wrapper">
        <SessionCompleteButton
          v-if="!!props.hasCompleteButton"
          :buttonProps="{
            completeButtonText: buttonText,
            completed,
            isForPathwayStep: false,
            buttonLink,
          }"
          :show-close-button="showCloseButton"
          @close="close"
          @complete="complete"
        />
        <a
          v-else
          class="btn btn-lg btn-primary journey-next-step"
          :href="buttonLink"
          :target="buttonLinkTarget"
          data-test="session-slide-button"
          >{{ buttonText }}</a
        >
        <p v-if="errorMessage" class="text-danger" style="margin-top: 2rem">{{ errorMessage }}</p>
      </div>
    </template>
    <template #visual>
      <VisualPositioningCanvas>
        <CmsLottieAnimation
          v-if="!!lottieSrc"
          ref="visual"
          :id="'lottie-' + props.index"
          :container-id="'slide' + props.index"
          :src="lottieSrc"
          :reverse-src="props.reverseLottieUrl"
        />
      </VisualPositioningCanvas>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import { useRoute } from "vue-router";
import cms from "../../../services/cms/cms";
import SessionSlide from "../../sessions/session-slide.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import CmsLottieAnimation from "../cms-lottie-animation.vue";
import CmsImg from "../cms-img.vue";
import {
  CmsLink,
  CmsSessionSlideWithButtonTextAndLottie,
  CmsSessionSlideWithCompleteButtonTextAndLottie,
  SessionType,
} from "../../../services/cms/cms-type-definitions";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import SlideText from "./slide-text.vue";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import { useToggle } from "../../../composables/useToggle";
import delay from "../../../services/utilities/delay";
import SessionCompleteButton, { CmsSessionDoc } from "../session-complete-button.vue";
import { csContext } from "../../../services/cs-context";
import cmsContentMap from "../../../services/cms/cms-content-map";
import { ResultApiModel } from "../../../api-models";
import useAxios from "../../../composables/api";
import { Analytics } from "../../../services/analytics/analytics";

const props = defineProps<{
  slice: PrismicSliceWithData;
  sessionType: SessionType;
  reverseLottieUrl?: string;
  hasCompleteButton?: boolean;
  doc?: CmsSessionDoc;
  index: number;
  closeUrl?: string;
}>();

const completed = ref(false);
const errorMessage = ref<string>();

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const prismicSlice = computed<CmsSessionSlideWithButtonTextAndLottie>(() => {
  return props.slice.prismicSlice as CmsSessionSlideWithButtonTextAndLottie;
});

const lottieSrc = computed(() => prismicSlice.value.primary.lottie_file.url);
const buttonText = computed(() => cms.asText(prismicSlice.value.primary.button_text) || "Complete");
const buttonLink = computed(
  () => cms.linkResolver(prismicSlice.value.primary.button_link) || props.closeUrl || undefined,
);
const buttonLinkTarget = computed(
  () => (prismicSlice.value.primary.button_link as CmsLink)?.target,
);
const showCloseButton = computed<boolean>(() => {
  return (
    (prismicSlice.value as unknown as CmsSessionSlideWithCompleteButtonTextAndLottie).primary
      ?.display_close_button ?? false
  );
});

const section = computed(() => {
  return props.slice.section;
});

const isFoundationsPathway = computed<boolean>(() => {
  const foundationPathwayUids = [
    cmsContentMap.staticRoutes.foundationsSessionUid,
    cmsContentMap.staticRoutes.foundationsCustomFrameworkSessionUid,
  ];

  return foundationPathwayUids.includes(props.doc?.uid ?? "");
});

async function postCompleteSession(url: string) {
  try {
    const { promise } = useAxios<ResultApiModel>(url, {
      httpMethod: "POST",
      isFatal: false,
    });
    const response = await promise;
    completed.value = response.success;
  } catch (error) {
    errorMessage.value = "Oops, something went wrong. Please try again.";
  }
}

const route = useRoute();
const levelTitle = computed(() => route.query.levelTitle?.toString().trim() ?? "");
const levelId = computed(() => route.query.levelId as string);
const levelUid = computed(() => route.query.levelUid as string);
const levelNumber = computed(() => route.query.levelNumber as string);

const closeLocation = (): string => {
  if (buttonLink.value) {
    return buttonLink.value;
  }

  // Check to see if user is a team lead and route accoridingly
  if (levelTitle.value?.length > 0) {
    return "/team/dashboard";
  }

  return props.sessionType === "level" ? "/team/dashboard" : "/";
};

const close = (): void => {
  window.location.href = closeLocation();
};

async function markLevelAsComplete() {
  try {
    await axios.post("/api/teamlevels/CompleteLevel", {
      title: decodeURIComponent(levelTitle.value),
      prismicId: levelId.value,
      prismicUid: levelUid.value,
      teamId: csContext.currentTeam?.id,
      levelNumber: +levelNumber.value,
    });
    completed.value = true;
  } catch (error) {
    errorMessage.value = (error as AxiosError).response?.data.message;
  }
}

const complete = async (): Promise<void> => {
  const delayPromise = delay(3000);
  if (isFoundationsPathway.value) {
    await postCompleteSession("/api/foundation/complete");
  } else if (props.sessionType === "level") {
    await markLevelAsComplete();
  } else {
    completed.value = true;
  }

  await delayPromise;
  if (!errorMessage.value) {
    window.location.href = closeLocation();
  }
};

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(section.value)) {
    toggleIsDisplayed();
  }
});

// These 3 refs need to be exposed so that cms-session can apply animations to them
const visual = ref<InstanceType<typeof CmsImg> | null>();
const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

defineExpose({
  visualDiv,
  visual,
  slide,
});
</script>
